import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Shield,
  Smartphone,
  Copy,
  Check,
  AlertCircle,
  Loader2,
  QrCode,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useTranslation } from '@/hooks/useTranslation';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';

interface TwoFactorSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type Step = 'initial' | 'setup' | 'verify' | 'success' | 'manage';

interface MFAFactor {
  id: string;
  friendly_name?: string;
  factor_type: string;
  status: string;
  created_at: string;
}

export default function TwoFactorSetupModal({
  isOpen,
  onClose,
  onSuccess,
}: TwoFactorSetupModalProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [step, setStep] = useState<Step>('initial');
  const [isLoading, setIsLoading] = useState(false);
  const [qrCode, setQrCode] = useState<string>('');
  const [secret, setSecret] = useState<string>('');
  const [factorId, setFactorId] = useState<string>('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [existingFactors, setExistingFactors] = useState<MFAFactor[]>([]);

  useEffect(() => {
    if (isOpen) {
      checkExistingMFA();
    }
  }, [isOpen]);

  const checkExistingMFA = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;

      const verifiedFactors = data.totp.filter((f) => f.status === 'verified');

      // Clean up any stale unverified factors so fresh enrollment works
      const unverifiedFactors = data.totp.filter((f) => f.status !== 'verified');
      for (const f of unverifiedFactors) {
        try {
          await supabase.auth.mfa.unenroll({ factorId: f.id });
        } catch {
          // best-effort cleanup
        }
      }

      setExistingFactors(verifiedFactors);

      if (verifiedFactors.length > 0) {
        setStep('manage');
      } else {
        setStep('initial');
      }
    } catch (err) {
      console.error('Error checking MFA status:', err);
      setStep('initial');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartSetup = async () => {
    setIsLoading(true);
    setError('');
    try {
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'Authenticator App',
      });

      if (error) throw error;

      if (data) {
        setQrCode(data.totp.qr_code);
        setSecret(data.totp.secret);
        setFactorId(data.id);
        setStep('setup');
      }
    } catch (err) {
      console.error('Error enrolling MFA:', err);
      setError(t('settings.2fa.enrollError') || 'Failed to start 2FA setup. Please try again.');
      toast({
        title: t('settings.2fa.error') || 'Error',
        description: err instanceof Error ? err.message : 'Failed to start 2FA setup',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async () => {
    if (verificationCode.length !== 6) {
      setError(t('settings.2fa.codeLength') || 'Please enter a 6-digit code');
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId,
      });

      if (challengeError) throw challengeError;

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code: verificationCode,
      });

      if (verifyError) throw verifyError;

      setStep('success');
      toast({
        title: t('settings.2fa.enabled') || '2FA Enabled',
        description:
          t('settings.2fa.enabledDesc') ||
          'Two-factor authentication has been enabled for your account.',
      });

      setTimeout(() => {
        onSuccess();
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Error verifying MFA:', err);
      setError(t('settings.2fa.invalidCode') || 'Invalid verification code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisable2FA = async (factorIdToRemove: string) => {
    setIsLoading(true);
    setError('');
    try {
      const { error } = await supabase.auth.mfa.unenroll({
        factorId: factorIdToRemove,
      });

      if (error) throw error;

      toast({
        title: t('settings.2fa.disabled') || '2FA Disabled',
        description:
          t('settings.2fa.disabledDesc') || 'Two-factor authentication has been disabled.',
      });

      await checkExistingMFA();
      onSuccess();
    } catch (err) {
      console.error('Error disabling MFA:', err);
      setError(t('settings.2fa.disableError') || 'Failed to disable 2FA. Please try again.');
      toast({
        title: t('settings.2fa.error') || 'Error',
        description: err instanceof Error ? err.message : 'Failed to disable 2FA',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleClose = async () => {
    // Unenroll the pending (unverified) factor so re-opening setup works
    if (factorId && (step === 'setup' || step === 'verify')) {
      try {
        await supabase.auth.mfa.unenroll({ factorId });
      } catch {
        // best-effort cleanup
      }
    }
    setStep('initial');
    setQrCode('');
    setSecret('');
    setFactorId('');
    setVerificationCode('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#16c47f]/10 rounded-xl flex items-center justify-center">
                <Shield className="w-5 h-5 text-[#16c47f]" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('settings.2fa.title') || 'Two-Factor Authentication'}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {step === 'manage'
                    ? t('settings.2fa.manageDesc') || 'Manage your 2FA settings'
                    : t('settings.2fa.subtitle') || 'Secure your account'}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition"
            >
              <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {isLoading && step === 'initial' ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-[#16c47f] animate-spin" />
              </div>
            ) : step === 'initial' ? (
              <div className="space-y-6">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <Smartphone className="w-5 h-5 text-[#16c47f] mt-0.5" />
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white mb-1">
                        {t('settings.2fa.howItWorks') || 'How it works'}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {t('settings.2fa.howItWorksDesc') ||
                          'Use an authenticator app like Google Authenticator or Authy to generate verification codes when you sign in.'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                    <div className="w-6 h-6 bg-[#16c47f]/10 rounded-full flex items-center justify-center text-[#16c47f] font-medium text-xs">
                      1
                    </div>
                    {t('settings.2fa.step1') || 'Download an authenticator app'}
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                    <div className="w-6 h-6 bg-[#16c47f]/10 rounded-full flex items-center justify-center text-[#16c47f] font-medium text-xs">
                      2
                    </div>
                    {t('settings.2fa.step2') || 'Scan the QR code with the app'}
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                    <div className="w-6 h-6 bg-[#16c47f]/10 rounded-full flex items-center justify-center text-[#16c47f] font-medium text-xs">
                      3
                    </div>
                    {t('settings.2fa.step3') || 'Enter the verification code'}
                  </div>
                </div>

                <Button
                  onClick={handleStartSetup}
                  disabled={isLoading}
                  className="w-full h-12 bg-[#16c47f] hover:bg-[#12b070] text-white font-medium rounded-xl"
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    t('settings.2fa.startSetup') || 'Start Setup'
                  )}
                </Button>
              </div>
            ) : step === 'setup' ? (
              <div className="space-y-6">
                <div className="text-center">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    {t('settings.2fa.scanQR') || 'Scan this QR code with your authenticator app'}
                  </p>
                  {qrCode && (
                    <div className="inline-block p-4 bg-white border border-gray-200 dark:border-gray-600 rounded-xl">
                      <img src={qrCode} alt="2FA QR Code" className="w-48 h-48" />
                    </div>
                  )}
                </div>

                <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
                  <Label className="text-xs text-gray-500 dark:text-gray-400 mb-2 block">
                    {t('settings.2fa.manualEntry') || "Can't scan? Enter this code manually:"}
                  </Label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-white dark:bg-gray-800 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 text-sm font-mono text-gray-900 dark:text-white break-all">
                      {secret}
                    </code>
                    <Button variant="outline" size="sm" onClick={copySecret} className="shrink-0">
                      {copied ? (
                        <Check className="w-4 h-4 text-[#16c47f]" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <Button
                  onClick={() => setStep('verify')}
                  className="w-full h-12 bg-[#16c47f] hover:bg-[#12b070] text-white font-medium rounded-xl"
                >
                  {t('settings.2fa.continue') || 'Continue'}
                </Button>
              </div>
            ) : step === 'verify' ? (
              <div className="space-y-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-[#16c47f]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <QrCode className="w-8 h-8 text-[#16c47f]" />
                  </div>
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                    {t('settings.2fa.enterCode') || 'Enter verification code'}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t('settings.2fa.enterCodeDesc') ||
                      'Enter the 6-digit code from your authenticator app'}
                  </p>
                </div>

                <div className="space-y-2">
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={verificationCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      setVerificationCode(value);
                      setError('');
                    }}
                    placeholder="000000"
                    className="text-center text-2xl tracking-widest font-mono h-14 bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white rounded-xl"
                  />
                  {error && (
                    <div className="flex items-center gap-2 text-red-600 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      {error}
                    </div>
                  )}
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setStep('setup')}
                    className="flex-1 h-12 rounded-xl"
                  >
                    {t('settings.2fa.back') || 'Back'}
                  </Button>
                  <Button
                    onClick={handleVerify}
                    disabled={isLoading || verificationCode.length !== 6}
                    className="flex-1 h-12 bg-[#16c47f] hover:bg-[#12b070] text-white font-medium rounded-xl"
                  >
                    {isLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      t('settings.2fa.verify') || 'Verify'
                    )}
                  </Button>
                </div>
              </div>
            ) : step === 'success' ? (
              <div className="text-center py-6">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="w-20 h-20 bg-[#16c47f]/10 rounded-full flex items-center justify-center mx-auto mb-4"
                >
                  <Check className="w-10 h-10 text-[#16c47f]" />
                </motion.div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  {t('settings.2fa.successTitle') || '2FA Enabled!'}
                </h3>
                <p className="text-gray-500 dark:text-gray-400">
                  {t('settings.2fa.successDesc') ||
                    'Your account is now protected with two-factor authentication.'}
                </p>
              </div>
            ) : step === 'manage' ? (
              <div className="space-y-6">
                <div className="bg-green-50 dark:bg-green-900/30 rounded-xl p-4 flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-green-900 dark:text-green-300">
                      {t('settings.2fa.activeTitle') || '2FA is Active'}
                    </h3>
                    <p className="text-sm text-green-700 dark:text-green-400">
                      {t('settings.2fa.activeDesc') ||
                        'Your account is protected with two-factor authentication.'}
                    </p>
                  </div>
                </div>

                {existingFactors.map((factor) => (
                  <div
                    key={factor.id}
                    className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-xl"
                  >
                    <div className="flex items-center gap-3">
                      <Smartphone className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {factor.friendly_name || 'Authenticator App'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {t('settings.2fa.addedOn') || 'Added on'}{' '}
                          {new Date(factor.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDisable2FA(factor.id)}
                      disabled={isLoading}
                      className="text-red-600 dark:text-red-400 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30 border-red-200 dark:border-red-800"
                    >
                      {isLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4 mr-1" />
                          {t('settings.2fa.remove') || 'Remove'}
                        </>
                      )}
                    </Button>
                  </div>
                ))}

                {error && (
                  <div className="flex items-center gap-2 text-red-600 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </div>
                )}

                <Button variant="outline" onClick={handleClose} className="w-full h-12 rounded-xl">
                  {t('settings.2fa.close') || 'Close'}
                </Button>
              </div>
            ) : null}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
